import React, { useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  useDisclosure,
  Button,
  useToast,
  Select,
  Textarea,
} from '@chakra-ui/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import TicketApi from 'api/ticket';

const validationSchema = Yup.object({
  subject: Yup.string().required('Subject is required'),
  message: Yup.string().required('Message is required'),
  priority: Yup.string().required('Priority is required'),
  status: Yup.string().required('Status is required'),
});

export default function EditModal({ initialData, func, Icon, head }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const initialRef = React.useRef(null);
  const finalRef = React.useRef(null);
  const toast = useToast();

  const formik = useFormik({
    initialValues: {
      subject: initialData?.subject || '',
      message: initialData?.message || '',
      priority: initialData?.priority || '',
      status: initialData?.status || '',
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const apiResponse = await TicketApi.Update({
          id: initialData.id,
          subject: values.subject,
          message: values.message,
          status: values.status,
          priority: values.priority,
        });

        if (apiResponse.data.status_code === 200) {
          toast({
            title: 'Successfully Updated',
            description: apiResponse.data.message,
            status: 'success',
            duration: 3000,
            isClosable: true,
            position: 'top',
          });
          onClose();
          func();
          resetForm();
        } else {
          throw new Error(apiResponse.data.message);
        }
      } catch (error) {
        console.log('Error', error);
        toast({
          title: 'Failed to update ticket',
          description: error.response?.data?.message || 'Unknown error',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (isOpen) {
      formik.setValues({
        subject: initialData.subject,
        message: initialData.message,
        priority: initialData.priority,
        status: initialData.status,
      });
    }
  }, [initialData, isOpen]);

  return (
    <>
      <Button variant='ghost' onClick={onOpen}>
        {Icon}
      </Button>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        initialFocusRef={initialRef}
        finalFocusRef={finalRef}
      >
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={formik.handleSubmit}>
            <ModalHeader>{head}</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <FormControl
                isInvalid={formik.touched.subject && formik.errors.subject}
              >
                <FormLabel>Subject</FormLabel>
                <Textarea
                  disabled
                  ref={initialRef}
                  {...formik.getFieldProps('subject')}
                />
                {formik.touched.subject && formik.errors.subject ? (
                  <p>{formik.errors.subject}</p>
                ) : null}
              </FormControl>
              <FormControl
                isInvalid={formik.touched.message && formik.errors.message}
              >
                <FormLabel>Message</FormLabel>
                <Textarea
                  disabled
                  {...formik.getFieldProps('message')}
                ></Textarea>
                {formik.touched.message && formik.errors.message ? (
                  <p>{formik.errors.message}</p>
                ) : null}
              </FormControl>
              <FormControl
                display='flex'
                alignItems='center'
                mt={4}
                isRequired
                isInvalid={formik.touched.priority && formik.errors.priority}
              >
                <FormLabel htmlFor='priority' mb='0'>
                  Priority
                </FormLabel>
                <Select
                  id='priority'
                  placeholder='Select Priority'
                  {...formik.getFieldProps('priority')}
                >
                  <option value='LOW'>Low</option>
                  <option value='NORMAL'>Normal</option>
                  <option value='HIGH'>High</option>
                </Select>
                {formik.touched.priority && formik.errors.priority ? (
                  <p>{formik.errors.priority}</p>
                ) : null}
              </FormControl>

              <FormControl
                display='flex'
                alignItems='center'
                mt={4}
                isRequired
                isInvalid={formik.touched.status && formik.errors.status}
              >
                <FormLabel htmlFor='status' mb='0'>
                  Status
                </FormLabel>
                <Select
                  id='status'
                  placeholder='Select Status'
                  {...formik.getFieldProps('status')}
                >
                  <option value='OPEN'>Open</option>
                  <option value='INPROGRESS'>In Progress</option>
                  <option value='CLOSED'>Closed</option>
                  <option value='RESOLVED'>Resolved</option>
                </Select>
                {formik.touched.status && formik.errors.status ? (
                  <p>{formik.errors.status}</p>
                ) : null}
              </FormControl>
            </ModalBody>

            <ModalFooter>
              <Button
                colorScheme='blue'
                mr={3}
                type='submit'
                disabled={formik.isSubmitting}
              >
                Save
              </Button>
              <Button onClick={onClose}>Cancel</Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </>
  );
}
